/**
 * Admin dashboard overview with key metrics, quick actions, and system status
 * Follows the Help Center UX pattern with quick action cards and stat widgets
 */

import { useMemo } from 'react';
import {
  DollarSign,
  ShoppingCart,
  Users,
  TrendingUp,
  MessageSquare,
  Clock,
  FileText,
  RotateCcw,
  RefreshCw,
  HelpCircle,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';
import { QuickActionsGrid, QuickAction } from '../common/QuickActionsGrid';
import { StatsWidget, StatItem } from '../common/StatsWidget';

interface AdminOverviewProps {
  stats: {
    // Basic counts
    totalUsers: number;
    totalProducts: number;
    totalOrders: number;
    // Product breakdown
    publishedProducts: number;
    draftProducts: number;
    // Order breakdown
    completedOrders: number;
    pendingOrders: number;
    refundedOrders: number;
    // Revenue
    totalRevenue: number;
    // Users
    adminUsers: number;
    // Contacts
    newContacts: number;
    // Rates
    conversionRate: number;
    refundRate: number;
  };
  loading: boolean;
  onRefresh: () => void;
}

export default function AdminOverview({
  stats,
  loading,
  onRefresh,
}: AdminOverviewProps): JSX.Element {
  const { canAny } = usePermission();

  // Quick actions for admin dashboard - filtered by permissions
  const quickActions: QuickAction[] = useMemo(() => {
    const actions: QuickAction[] = [];

    if (canAny(['product:admin', 'product:*', 'product:create'])) {
      actions.push({
        id: 'add-product',
        title: 'Add Product',
        description: 'Create a new product listing',
        icon: 'plus',
        linkUrl: '/admin?tab=products',
        buttonText: 'Create Product',
        variant: 'primary',
      });
    }

    if (canAny(['order:admin', 'order:*', 'order:read'])) {
      actions.push({
        id: 'view-orders',
        title: 'View Orders',
        description: 'Manage and process customer orders',
        icon: 'shopping-bag',
        linkUrl: '/admin?tab=orders',
        buttonText: 'View Orders',
        variant: 'success',
      });
    }

    if (canAny(['user:admin', 'user:*'])) {
      actions.push({
        id: 'manage-users',
        title: 'Manage Users',
        description: 'View and manage user accounts',
        icon: 'users',
        linkUrl: '/admin?tab=users',
        buttonText: 'View Users',
        variant: 'warning',
      });
    }

    if (canAny(['analytics:admin', 'analytics:*'])) {
      actions.push({
        id: 'view-analytics',
        title: 'View Analytics',
        description: 'Sales and performance metrics',
        icon: 'bar-chart',
        linkUrl: '/admin?tab=analytics',
        buttonText: 'View Analytics',
        variant: 'default',
      });
    }

    // New quick actions
    if (canAny(['contact:read', 'contact:*', 'settings:admin'])) {
      actions.push({
        id: 'view-contacts',
        title: 'View Contacts',
        description: 'Respond to customer inquiries',
        icon: 'mail',
        linkUrl: '/admin?tab=contact',
        buttonText: 'View Messages',
        variant: 'default',
      });
    }

    if (canAny(['user:admin', 'user:*'])) {
      actions.push({
        id: 'manage-roles',
        title: 'Manage Roles',
        description: 'Configure roles and permissions',
        icon: 'shield',
        linkUrl: '/admin?tab=roles',
        buttonText: 'View Roles',
        variant: 'default',
      });
    }

    if (canAny(['help:admin', 'help:*'])) {
      actions.push({
        id: 'manage-help',
        title: 'Help Center',
        description: 'Manage help articles and FAQs',
        icon: 'help-circle',
        linkUrl: '/admin?tab=help',
        buttonText: 'Manage Help',
        variant: 'default',
      });
    }

    if (canAny(['settings:admin', 'settings:*'])) {
      actions.push({
        id: 'settings',
        title: 'Settings',
        description: 'Platform configuration options',
        icon: 'settings',
        linkUrl: '/admin?tab=settings',
        buttonText: 'View Settings',
        variant: 'default',
      });
    }

    return actions;
  }, [canAny]);

  // Primary business metrics
  const businessMetrics: StatItem[] = useMemo(() => [
    {
      id: 'revenue',
      label: 'Total Revenue',
      value: `$${(stats.totalRevenue ?? 0).toLocaleString()}`,
      icon: DollarSign,
      variant: 'success',
      linkUrl: '/admin?tab=analytics',
      linkText: 'View analytics',
    },
    {
      id: 'orders',
      label: 'Total Orders',
      value: stats.totalOrders ?? 0,
      icon: ShoppingCart,
      variant: 'primary',
      linkUrl: '/admin?tab=orders',
      linkText: 'View orders',
    },
    {
      id: 'users',
      label: 'Total Users',
      value: stats.totalUsers ?? 0,
      icon: Users,
      variant: 'primary',
      linkUrl: '/admin?tab=users',
      linkText: 'View users',
    },
    {
      id: 'conversion',
      label: 'Conversion Rate',
      value: `${stats.conversionRate ?? 0}%`,
      icon: TrendingUp,
      variant: 'default',
      linkUrl: '/admin?tab=analytics',
      linkText: 'View analytics',
    },
  ], [stats]);

  // Attention items - things that may need action
  const newContacts = stats.newContacts ?? 0;
  const pendingOrders = stats.pendingOrders ?? 0;
  const draftProducts = stats.draftProducts ?? 0;
  const refundRate = stats.refundRate ?? 0;

  const attentionItems: StatItem[] = useMemo(() => [
    {
      id: 'new-contacts',
      label: 'New Contacts',
      value: newContacts,
      icon: MessageSquare,
      variant: newContacts > 0 ? 'error' : 'default',
      linkUrl: '/admin?tab=contact',
      linkText: 'View messages',
    },
    {
      id: 'pending-orders',
      label: 'Pending Orders',
      value: pendingOrders,
      icon: Clock,
      variant: pendingOrders > 0 ? 'warning' : 'default',
      linkUrl: '/admin?tab=orders',
      linkText: 'View orders',
    },
    {
      id: 'draft-products',
      label: 'Draft Products',
      value: draftProducts,
      icon: FileText,
      variant: 'default',
      linkUrl: '/admin?tab=products',
      linkText: 'View products',
    },
    {
      id: 'refund-rate',
      label: 'Refund Rate',
      value: `${refundRate}%`,
      icon: RotateCcw,
      variant: refundRate > 5 ? 'warning' : 'default',
      linkUrl: '/admin?tab=analytics',
      linkText: 'View analytics',
    },
  ], [newContacts, pendingOrders, draftProducts, refundRate]);

  // System status items
  const systemStatus = [
    { id: 'platform', label: 'Platform Status', status: 'operational', detail: 'All systems operational' },
    { id: 'payments', label: 'Payment Processing', status: 'operational', detail: 'Stripe active' },
    { id: 'email', label: 'Email Service', status: 'operational', detail: 'Resend active' },
    { id: 'storage', label: 'Storage', status: 'operational', detail: 'Firebase Storage' },
  ];

  return (
    <div className="space-y-8">
      {/* Header with Refresh Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Dashboard Overview
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Welcome back! Here's what's happening with your store.
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="btn btn-secondary inline-flex items-center gap-2"
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Business Metrics Section */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Business Metrics
        </h3>
        <StatsWidget stats={businessMetrics} columns={4} loading={loading} />
      </section>

      {/* Attention Items Section */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Needs Attention
        </h3>
        <StatsWidget stats={attentionItems} columns={4} loading={loading} />
      </section>

      {/* Quick Actions Section */}
      {quickActions.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Quick Actions
          </h3>
          <QuickActionsGrid
            actions={quickActions}
            columns={quickActions.length >= 4 ? 4 : quickActions.length as 2 | 3 | 4}
          />
        </section>
      )}

      {/* System Status Section */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          System Status
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          {systemStatus.map(item => (
            <div key={item.id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {item.status === 'operational' ? (
                  <CheckCircle size={20} className="text-green-500" />
                ) : (
                  <AlertCircle size={20} className="text-amber-500" />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {item.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {item.detail}
                  </p>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                item.status === 'operational'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              }`}>
                {item.status === 'operational' ? 'Operational' : 'Degraded'}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help managing the platform?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Check out the help center for admin guides and documentation
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}
